import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';

interface ISliderData {
	boundsComputedAtLeastOneTime: boolean;
	renderingUpdateNeeded: boolean;
	isReplicatorTriggeredAction: boolean;
	replicator: ReplicatorComponent | null;
	replicationListener: STD.events.Listener<any, any> | null;
	replicaUpdateListener: STD.events.Listener<any, any> | null;

	onDraggingDebounced(
		this: SliderRenderingMode,
		newValue: number
	): void;

	plotsDOMs: HTMLDivElement[];
	replicatedDOMPlot: HTMLDivElement | null;
};

class SliderRenderingMode extends CM.Plugin.PluginWithData<
	InputNumberComponent,
	ISliderData
> {
	id = 'slider';
	instances = new Set<InputNumberComponent>;
	resizeObserver = new ResizeObserver(this.onResized.bind(this));
	/**
	 * The click of the dragend should be intercepted and canceled.
	 **/
	clickAwaited = false;

	generateData(component: InputNumberComponent) {
		return ({
			boundsComputedAtLeastOneTime: false,
			renderingUpdateNeeded: true,
			replicator: null,
			onDraggingDebounced: STD.misc.createDebouncer({
				fn: <() => void><any>component.updateValue.bind(component),
				delay: component.component_configuration.debouncerDelay || 0
			}),
			isReplicatorTriggeredAction: false,
			replicationListener: null,
			replicaUpdateListener: null,
			plotsDOMs: [],
			replicatedDOMPlot: null
		});
	};

	private async onUpdated(ev: STD.events.Event<InputNumberComponent, CM.Component<any, any>>) {
		const component = ev.target;
		await this.updateHandleFromInternalValue(component);
	};

	override attach(component: InputNumberComponent) {
		super.attach(component);
		this.instances.add(component);
		component.component_DOM.dataset.renderingMode = this.id;
		component.component_DOM.append(...[
			{
				classList: ['ruler-plot']
			}, {
				classList: ['ruler']
			}, {
				classList: ['ruler-plot']
			}, {
				classList: ['handle'],
				events: {
					pointerdown: this.onDragstart.bind(this, component)
				}
			}
		].map(x => STD.DOM.factory(x)));
		component.component_DOM.addEventListener('click', this.onClick.bind(this, component));
		const data = this.getData(component);
		data.replicationListener = new STD.events.Listener<any, CM.Component<any, any>, CM.Component.IListenEvent>({
			listener: component,
			isCapture: false,
			isAtTarget: false,
			type: 'replication',
			subType: '*',
			callback: this.onReplication.bind(this, component),
			disableEventExistsCheck: true,
			implementationInfos: {
				event: 'replication',
				subEvent: '*',
				actions: [],
				filter: null,
				bypassIfListenerDisabled: true
			},
			target: component
		});
		data.replicaUpdateListener = new STD.events.Listener<any, CM.Component<any, any>, CM.Component.IListenEvent>({
			listener: component,
			isCapture: false,
			isAtTarget: false,
			type: 'replica-update',
			callback: this.onReplicaUpdate.bind(this, component),
			disableEventExistsCheck: true,
			implementationInfos: {
				event: 'replica-update',
				actions: [],
				filter: null,
				bypassIfListenerDisabled: true
			},
			target: component
		});
		component.addEventListener('replication', data.replicationListener);
		component.addEventListener('replica-update', data.replicaUpdateListener);
		this.resizeObserver.observe(component.component_DOM);
		component.addEventListener('value', this.onUpdated, {
			listener: component,
			subType: component.static.type
		});
	};

	override detach(component: InputNumberComponent) {
		delete component.component_DOM.dataset.renderingMode;
		this.instances.delete(component);
		const data = this.getData(component);
		if (data.replicationListener) {
			component.removeEventListener(data.replicationListener);
		}
		super.detach(component);
	};

	private async onReplication(component: InputNumberComponent, event: STD.events.Event<any, any>) {
		const data = this.getData(component);
		if (event.subType === 'enable') {
			data.replicator = event.details.replicator;
			await component.updateConstraints(event);
		} else {
			data.replicator = null;
		}
	};

	private async onReplicaUpdate(component: InputNumberComponent, event: STD.events.Event<any, any>) {
		const data = this.getData(component);
		void event;
		if (data.renderingUpdateNeeded) {
			await this.updateHandleFromInternalValue(component);
		}
	};


	private oldBodyUserSelectStyle: string = '';
	private inUse: InputNumberComponent | null = null;

	private async onResized(entries: { target: Element }[]) {
		const instances = (
			Array.from(this.instances).
			filter(x => entries.find(y => x.component_DOM === y.target)).
			filter(x => !x.component_isDisabled)
		);
		for (const instance of instances) {
			await this.updateHandleFromInternalValue(instance);
		}
	};

	private async onClick(component: InputNumberComponent, nativeEvent: MouseEvent) {
		const data = this.getData(component);
		data.isReplicatorTriggeredAction = (<any>event)?.details?.fromReplicat != null;
		const t = <HTMLDivElement>nativeEvent.target;
		if (!t.classList.contains('ruler-plot')) {
			return;
		}
		const plotDOM = t;
		this.laidHandleOnPlot(component, plotDOM);
		const newValueRaw = (<Record<string, any>>t.details).value;
		const newValue = STD.math.round(newValueRaw, <number>component.component_configuration.accuracity);
		if (component.component_configuration.debouncerDelay) {
			await (<any>data).onDraggingDebounced(newValue);
		} else {
			await component.updateValue(newValue);
		}
		data.isReplicatorTriggeredAction = false;
	};

	async boundsUpdated(component: InputNumberComponent, stepsCountDiff: number) {
		const data = this.getData(component);
		const absDiff = Math.abs(stepsCountDiff);
		if (stepsCountDiff < 0) { //More element than before
			for (let i = 0; i < absDiff; i++) {
				component.component_DOM.insertBefore(
					STD.DOM.factory({
						classList: ['ruler-plot'],
						details: {}
					}),
					<HTMLElement>component.component_DOM.children[1]
				);
				component.component_DOM.insertBefore(
					STD.DOM.factory({classList: ['ruler']}),
					<HTMLElement>component.component_DOM.children[1]
				);
			}
		} else { //Less element than before
			for (let i = 0; i < absDiff; i++) {
				(<any>component.component_DOM.children)[0].remove();
				(<any>component.component_DOM.children)[0].remove();
			}
		}
		data.plotsDOMs = Array.from(component.component_DOM.querySelectorAll('.ruler-plot'));
		let ii = component.min;
		for (const plotDOM of data.plotsDOMs) {
			plotDOM.title = STD.math.round(ii, <number>component.component_configuration.accuracity).toString();
			(<Record<string, any>>plotDOM.details).value = ii;
			ii += component.step;
		}
		if (data.plotsDOMs.length === 1) {
			component.component_DOM.style.display = 'none';
		} else {
			component.component_DOM.style.display = '';
		}
		data.boundsComputedAtLeastOneTime = true;
		await this.updateHandleFromInternalValue(component);
		if ((stepsCountDiff !== 0) && data.replicator) {
			await data.replicator.notifyUpdate();
		}
	};

	private laidHandleOnPlot(component: InputNumberComponent, plotDOM: HTMLDivElement) {
		const data = this.getData(component);
		const handleDOM = component.component_DOM.querySelector('.handle') as HTMLDivElement;
		let containerWidth = (<HTMLElement>handleDOM.offsetParent)?.offsetWidth;
		let referencePlot = plotDOM;
		if (!containerWidth) {
			if (data.replicator) {
				let DOM = <HTMLElement>data.replicator.getReplicatedNodeFromOriginalComponent(component);
				if (DOM) {
					containerWidth = (<any>DOM).offsetWidth;
					if (!containerWidth) {
						containerWidth = 320;
					}
					referencePlot = (<any[]>Array.from(DOM.children)).find(x => x.title === plotDOM.title);
					if (!referencePlot) {
						const newDOM = component.component_DOM.cloneNode(true);
						DOM.replaceWith(newDOM);
						DOM = <any>newDOM;
						referencePlot = (<any[]>Array.from(DOM.children)).find(x => x.title === plotDOM.title);
					}
				} else {
				}
			} else {
			}
			if (!containerWidth) {
				// return;
			}
		}
		const handlePositionFromLeftInPixel = referencePlot.offsetLeft - (handleDOM.offsetWidth / 2);
		const handlePositionFromLeftInPercent = STD.math.computeRangePercentFromPosition(
			handlePositionFromLeftInPixel,
			0,
			containerWidth
		);
		if (handlePositionFromLeftInPixel === 0 && !STD.DOM.isVisible(referencePlot)) {
			data.renderingUpdateNeeded = true;
		} else {
			data.renderingUpdateNeeded = false;
		}
		handleDOM.style.left = handlePositionFromLeftInPercent + '%';
		if (data.replicator && !data.renderingUpdateNeeded) {
			void data.replicator.notifyUpdate();
		}
	};

	private async updateHandleFromInternalValue(component: InputNumberComponent) {
		const data = this.getData(component);
		if (!data.boundsComputedAtLeastOneTime) {
			return;
		}
		if (!component.component_isVisible) {
			data.renderingUpdateNeeded = true;
			// return;
		}
		if (typeof component.value !== 'number') {
			component.component_throwUserError(
				'Value is not a number!',
				{value: component.value}
			);
		}
		const v = component.value;
		const plotIdx = Math.round((v - component.min) / component.step);
		const plotDOM = data.plotsDOMs[plotIdx];
		if (!plotDOM) {
			const min = component.min;
			const max = component.max;
			if ((v < min) || (v > max)) {
				let defaultValue = null;
				if (component.defaultValue) {
					const v = component.defaultValue;
					if ((v >= min) && (v <= max)) {
						defaultValue = v;
					}
				}
				if (defaultValue === null) { // Try to center the default
					if ((min * -1) === max) { // min is inverse of max so center is 0
						defaultValue = 0;
					} else { // center is max - min
						defaultValue = (Math.max(max, min) - Math.min(min, max));
					}
				}
				// If previous formulas de not match valid default value then set the default to the min
				if (!((defaultValue >= min) && (defaultValue <= max))) {
					defaultValue = min;
				}
				await component.updateValue(defaultValue);
				await this.updateHandleFromInternalValue(component);
				return;
			} else {
				component.component_throwUserError('', {
					v,
					min,
					max,
					plotIdx
				});
			}
		}
		this.laidHandleOnPlot(component, plotDOM);
	};

	private async onDraggingDebounced(component: InputNumberComponent, newValue: number) {
		const data = this.getData(component);
		await <any>void 1;
		data.onDraggingDebounced.call(this, newValue);
	};

	private onDragstart(
		component: InputNumberComponent,
		event: Event
	) {
		const data = this.getData(component);
		data.isReplicatorTriggeredAction = (<any>event)?.details?.fromReplicat != null;
		this.inUse = component;
		const handleDOM = <HTMLDivElement>component.component_DOM.querySelector('.handle');
		handleDOM.style.cursor = 'grabbing';
		this.oldBodyUserSelectStyle = document.body.style.userSelect;
		const selection = window.getSelection();
		if (selection && (selection.type !== 'None')) {
			selection.removeAllRanges();
		}
		if (this.oldBodyUserSelectStyle === 'none') {
			this.oldBodyUserSelectStyle = '';
		}
		component.component_DOM.style.pointerEvents = 'all';
		if (CM.Manager.root instanceof CM.DOMComponent) {
			CM.Manager.root.component_DOM.style.pointerEvents = 'none';
		}
		document.body.style.userSelect = 'none';
	};

	private isAlreadyInDraggHandling = false;

	async onDragging(nativeEvent: MouseEvent) {
		const current = this.inUse;
		if (!current) {
			return;
		}
		if (this.isAlreadyInDraggHandling) {
			await this.onDragging(nativeEvent);
			return;
		}
		this.isAlreadyInDraggHandling = true;
		try {
			const data = this.getData(current);
			await current.dispatchEvent('dragging');
			document.body.style.cursor = 'move';
			const bx = nativeEvent.clientX;
			const x = bx - 10;
			const x2 = x + 9; // x + (handleSize / 2)
			const realPlotsDOMs = data.plotsDOMs;
			if (!data.replicator) {
				data.isReplicatorTriggeredAction = false;
			}
			const plotsDOMs = data.isReplicatorTriggeredAction ? <HTMLDivElement[]>Array.from(
				data.replicator?.component_querySelectorAll(`[data-component-id='${current.id}'][data-component-uid='${current.component_uid}'] .ruler-plot`) ||
				realPlotsDOMs || []
			) : realPlotsDOMs;
			/** [offset, index][] **/
			const dists = plotsDOMs.map<[number, number]>((plotDOM, i) => [
				Math.abs(STD.DOM.getOffset(plotDOM).left - x2),
				i
			]);
			const min = dists.reduce(
				(a, c) => (<number>c[0] < <number>a[0]) ? c : a,
				[Infinity, -1]
			);
			const min1 = min[1];
			const plotDOM = realPlotsDOMs[min1];
			if (!plotDOM) {
				throw STD.errors.user('Found no plot for the selected value!', {
					current,
					realPlotsDOMs,
					min1,
					data,
					plotsDOMs
				});
			}
			this.laidHandleOnPlot(current, plotDOM);
			const newValue = STD.math.round(
				current.min + (min1 * current.step),
				<number>current.component_configuration.accuracity
			);
			if (current.component_configuration.debouncerDelay) {
				await this.onDraggingDebounced(current, newValue);
			} else {
				await current.updateValue(newValue);
			}
			this.isAlreadyInDraggHandling = false;
		} catch (e) {
			this.isAlreadyInDraggHandling = false;
			throw e;
		}
	};

	async onDraggend(event: Event) {
		if (!this.inUse) {
			return;
		}
		void event;
		this.clickAwaited = true;
		const self = this.inUse;
		const handleDOM = self.component_DOM.querySelector('.handle') as HTMLDivElement;
		handleDOM.style.cursor = '';
		document.body.style.cursor = '';
		if (CM.Manager.root instanceof CM.DOMComponent) {
			CM.Manager.root.component_DOM.style.pointerEvents = '';
		}
		self.component_DOM.style.pointerEvents = '';
		document.body.style.userSelect = this.oldBodyUserSelectStyle;
		this.oldBodyUserSelectStyle = '';
		await this.inUse.dispatchEvent('draggend');
		this.inUse = null;
	};

	onDocumentClick(event: Event) {
		if (this.clickAwaited) {
			this.clickAwaited = false;
			event.stopImmediatePropagation();
			event.stopPropagation();
			event.preventDefault();
		}
	};

	constructor() {
		super();
		this.onUpdated = this.onUpdated.bind(this);
		this.onDragging = STD.misc.createDebouncerPromise({
			fn: this.onDragging.bind(this),
			delay: 50
		});
		document.body.addEventListener(
			'pointermove',
			this.onDragging,
			true
		);
		document.body.addEventListener(
			'pointerup',
			this.onDraggend.bind(this),
			true
		);
		document.addEventListener(
			'click',
			this.onDocumentClick.bind(this),
			true
		);
	};
}

export default SliderRenderingMode
;
import {InputNumberComponent} from '../InputNumber';
import {ReplicatorComponent} from '../../../Replicator/Replicator';
import './Slider.scss';
